import { z } from 'zod';

import { isoDateTimeSchema, positiveIntSchema, uuidSchema } from '../common/primitives';

/**
 * Die Rechtstexte, im Adminportal pflegbar (Entscheidung Q2).
 *
 * ## Warum versioniert
 *
 * Weil ein Kunde bei seiner Bestellung einer **bestimmten** Fassung zugestimmt
 * hat. Ein überschriebener Text macht diese Zustimmung unbelegbar — und genau
 * das ist der Fall, in dem man sie braucht. Ab Stufe 5 hält die Bestellung die
 * Versionsnummer fest; die Tabelle ist deshalb schon jetzt versioniert, obwohl
 * es die Bestellung noch nicht gibt. Nachträglich ginge es nicht: Für die
 * bereits geschriebenen Fassungen gäbe es keine Nummern.
 *
 * ## Warum kein Markdown-Renderer im Master
 *
 * Der Master liefert den Text so aus, wie er hineingeschrieben wurde. Gerendert
 * wird im Portal. Andernfalls wäre der Master an einer HTML-Ausgabe beteiligt,
 * und die Frage nach der Bereinigung des Markups stünde ausgerechnet dort, wo
 * es keine Oberfläche gibt.
 */

export const LEGAL_DOCUMENT_KEYS = {
  imprint: 'imprint',
  privacy: 'privacy',
  terms: 'terms',
  /** Widerrufsbelehrung — nur für Privatkunden relevant, ab Stufe 5. */
  withdrawal: 'withdrawal',
  /** Auftragsverarbeitungsvertrag, steht zum Herunterladen bereit (Q1). */
  dpa: 'dpa',
} as const;

export type LegalDocumentKey = (typeof LEGAL_DOCUMENT_KEYS)[keyof typeof LEGAL_DOCUMENT_KEYS];

export const legalDocumentKeySchema = z.enum([
  LEGAL_DOCUMENT_KEYS.imprint,
  LEGAL_DOCUMENT_KEYS.privacy,
  LEGAL_DOCUMENT_KEYS.terms,
  LEGAL_DOCUMENT_KEYS.withdrawal,
  LEGAL_DOCUMENT_KEYS.dpa,
]);

export const legalDocumentViewSchema = z.object({
  id: uuidSchema,
  key: legalDocumentKeySchema,
  /** Fortlaufend je Schlüssel, beginnend bei 1. */
  version: positiveIntSchema,
  title: z.string().min(1).max(160),
  bodyMarkdown: z.string(),
  publishedAt: isoDateTimeSchema,
});

export type LegalDocumentView = z.infer<typeof legalDocumentViewSchema>;

/**
 * Eine neue Fassung.
 *
 * Es gibt kein Ändern und kein Löschen — nur ein Hinzufügen. Die Nummer vergibt
 * der Master, nicht der Aufrufer: Zwei gleichzeitig gespeicherte Fassungen
 * bekämen sonst dieselbe.
 */
export const saveLegalDocumentSchema = z.object({
  title: z.string().min(1).max(160),
  bodyMarkdown: z.string().min(1).max(200_000),
});

export type SaveLegalDocumentInput = z.infer<typeof saveLegalDocumentSchema>;
